import React from 'react';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems?: number;
  itemsPerPage?: number;
  showItemsInfo?: boolean;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  itemsPerPage,
  showItemsInfo = true,
}: PaginationProps) {
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const getItemsRange = () => {
    if (!totalItems || !itemsPerPage) return '';
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, totalItems);
    return `${start}-${end} de ${totalItems}`;
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}
    >
      {showItemsInfo && totalItems && itemsPerPage && (
        <span
          style={{
            color: 'var(--muted-foreground)',
            fontSize: 14,
          }}
        >
          Mostrando {getItemsRange()} itens
        </span>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--border)',
            background: 'var(--background)',
            color: 'var(--foreground)',
            borderRadius: 6,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            opacity: currentPage === 1 ? 0.5 : 1,
            fontWeight: 500,
            fontSize: 14,
            transition: 'all 0.2s ease',
          }}
        >
          Anterior
        </button>

        {getPageNumbers().map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span
                style={{
                  padding: '8px 12px',
                  color: 'var(--muted-foreground)',
                }}
              >
                ...
              </span>
            ) : (
              <button
                onClick={() => onPageChange(page as number)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid var(--border)',
                  background: currentPage === page ? 'var(--primary)' : 'var(--background)',
                  color: currentPage === page ? 'white' : 'var(--foreground)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: currentPage === page ? 600 : 500,
                  fontSize: 14,
                  minWidth: 40,
                  transition: 'all 0.2s ease',
                }}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--border)',
            background: 'var(--background)',
            color: 'var(--foreground)',
            borderRadius: 6,
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            opacity: currentPage === totalPages ? 0.5 : 1,
            fontWeight: 500,
            fontSize: 14,
            transition: 'all 0.2s ease',
          }}
        >
          Próxima
        </button>
      </div>
    </div>
  );
}
